import React, { useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { CustomCheckin } from '@/components/organisms/self-checkin/custom-checkin'
import { checkValidateGuidesRequired, addValidateGuidesRequired, changeValidateGuidesRequired, Guide } from '@/utils/guide'
import { useSelfCheckInState } from '@/hooks/use-self-check-in-state'
import { NationalityType } from '@/models/self-checkin/accommodation-info'
import { CancelSpaceReservationFromSelfCI, fetchTimeSelectionSpace } from '@/apis/aipass'
import { SelectableTimeSelectionType, SelectedTime, SpaceAssign } from '@/models/self-checkin/custom-checkin'
import { LoaderContextCreator } from '@/contexts/loader'
import { TimeSelectionForm } from '@/components/organisms/self-checkin/time-selection-form'
import { SelfCheckinLayout } from '@/components/layouts/self-checkin-layout'
import { checkActivePlugin, PluginCode } from '@/libs/plugins'
import { ConfirmationWhoForm } from '@/components/organisms/self-checkin/confirmiation-who-form'
import { SelfCheckinContentCard } from '@/components/molecules/self-checkin-content-card'

export const CheckinCustomize: React.FC<{}> = () => {
  const { t } = useTranslation()
  const { isLoading, setIsLoading } = useContext(LoaderContextCreator())
  const history = useHistory()
  const { state, saveState, stateErrorHandling } = useSelfCheckInState()
  const hasCallingOperatorPlugin = checkActivePlugin(state.setting?.plugin, PluginCode.CallingOperator)

  const [guides, setGuides] = useState<Guide[]>([])
  const [validGuides, setValidGuides] = useState<any>([])
  const [timeSelections, setTimeSelections] = useState<SelectableTimeSelectionType[]>([])
  const [selectedInputValue, setSelectedInputValue] = useState<any>([])
  const [isInitializeComponent, setIsInitializeComponent] = useState(false)
  const [isRequiredTimeSelection, setIsRequiredTimeSelection] = useState(false)
  const [isValidateHotelCode, setIsValidateHotelCode] = useState(false)
  const [selectedTimes, setSelectedTimes] = useState<SelectedTime>({})
  const [assignSpaces, setAssignSpaces] = useState<SpaceAssign[]>([])

  const filterUniqueValueById = selectedInputValue => {
    const customCheckinIds = selectedInputValue.map(v => {
      return v.customCheckinId
    })
    return selectedInputValue.filter((v, index) => {
      // keep the last choice
      return customCheckinIds.lastIndexOf(v.customCheckinId) === index
    })
  }

  const onChangeState = (e: React.ChangeEvent<HTMLSelectElement> | React.ChangeEvent<HTMLTextAreaElement>, customCheckinId) => {
    if (!e.target.value) {
      const addValidateGuides = addValidateGuidesRequired(customCheckinId, guides)
      if (addValidateGuides.length > 0) {
        const errorMessage = document.getElementById(addValidateGuides[0]['customCheckinId'] + '-error')
        if (errorMessage) {
          errorMessage.style.visibility = errorMessage ? 'visible' : ''
        }
      }
      setValidGuides(addValidateGuides.concat(validGuides))
    } else {
      const changeValidateGuides = changeValidateGuidesRequired(customCheckinId, validGuides)
      const errorMessage = document.getElementById(customCheckinId + '-error')
      if (errorMessage) {
        errorMessage.style.visibility = errorMessage.style.visibility === 'visible' ? 'hidden' : ''
      }
      setValidGuides(changeValidateGuides)
    }
    const value = e.target.value
    const name = e.target.name
    const newSlectedInputValue = [...selectedInputValue, { customCheckinId: customCheckinId, [name]: value }]
    setSelectedInputValue(filterUniqueValueById(newSlectedInputValue))
  }

  // Replace form content with input content, not filled in with -
  const replaceInputValue = () => {
    const _guides = [...guides]
    _guides.map(guide => {
      selectedInputValue.map(v => {
        if (v.customCheckinId === guide.customCheckinId) {
          // When you reselect Please select
          if (v.value === '') {
            guide.selectedAnswer = '-'
          } else {
            guide.selectedAnswer = v.value
          }
        }
      })
      // when not choosing
      if (!guide.selectedAnswer) {
        guide.selectedAnswer = '-'
      }
    })
    setGuides(_guides || [])
  }

  const fetchTimeSelection = async (): Promise<SelectableTimeSelectionType[]> => {
    if (state.selectReservation?.hasSmartCheckin || !checkActivePlugin(state.setting?.plugin, PluginCode.CheckinTimeSelection)) {
      return []
    }

    if (!state.selectReservation || state.selectReservation?.selectedReservationAndRooms.length === 0) {
      return []
    }

    setIsLoading(true)
    await CancelSpaceReservationFromSelfCI({
      hotelId: state.hotelId,
      reservationIdWithAssignIds: state.selectReservation.selectedReservationAndRooms,
    })

    const selectedReservationIdsWithRoom = state.selectReservation.selectedReservationAndRooms
      .filter(r => r.guestRoomAssignIds.length > 0)
      .map(r => r.reservationId)
    let res: SelectableTimeSelectionType[] = []
    if (selectedReservationIdsWithRoom.length) {
      res = (await fetchTimeSelectionSpace(state.hotelId, selectedReservationIdsWithRoom)) || []
    }
    setTimeSelections(res)
    setIsLoading(false)
    return res
  }

  const isDisabled = (): boolean => {
    const _isValidHotelCode = hasCallingOperatorPlugin && !isValidateHotelCode
    const isRequiredGuides = validGuides.length > 0
    return isRequiredGuides || isRequiredTimeSelection || isLoading || _isValidHotelCode
  }

  const nextPath = () => {
    const isForeign = state.accommodationInfo?.nationality !== NationalityType.Jp
    return `/self-checkin/identity-verify/${isForeign ? 'upload-passport' : 'upload-license'}`
  }

  const onSubmit = async () => {
    replaceInputValue()
    const nav = t('Information from the facility')
    const notes = guides.map(guide => {
      if (guide.inputType !== 'none') {
        return `${guide.jpTitle}:${guide.selectedAnswer}`
      }
    })
    const filteredNotes = notes.filter(v => v)
    let checkinMemo: string = ''
    if (filteredNotes.length !== 0) {
      checkinMemo = [nav, ...filteredNotes].join('\n')
    } else {
      checkinMemo = ''
    }

    saveState({
      ...state,
      checkinCustomize: {
        guides,
        checkinMemo,
        assignSpaces,
        selectedTime: selectedTimes,
        timeSelections,
      },
    })
    history.push({ pathname: nextPath() })
  }

  const initializeComponent = async () => {
    window.scrollTo(0, 0)
    if (!stateErrorHandling(state, 'setting')) {
      return
    }

    // 事前CI済み: 時間選択・CIカスタムは回答済みのため初期化なし
    if (state.selectReservation?.hasSmartCheckin) {
      if (!hasCallingOperatorPlugin) {
        history.replace({ pathname: nextPath() })
        return
      }
      setIsInitializeComponent(true)
      return
    }

    // 在庫確認のために常に取得する
    const timeSelections = await fetchTimeSelection()
    const hasTimeSelection = timeSelections.length > 0
    const hasCustomCheckin = state.setting.customCheckin.length > 0

    const shouldShowUI = hasTimeSelection || hasCustomCheckin || hasCallingOperatorPlugin
    if (!shouldShowUI) {
      history.replace({ pathname: nextPath() })
      return
    }

    if (state.checkinCustomize) {
      setGuides(state.checkinCustomize.guides)
    } else {
      const isRequiredGuides = checkValidateGuidesRequired(state.setting.customCheckin)
      setValidGuides(isRequiredGuides)
      setGuides(state.setting.customCheckin)
    }

    setIsInitializeComponent(true)
  }

  useEffect(() => {
    initializeComponent()
  }, [])

  if (!isInitializeComponent) {
    return <></>
  }

  const isSmartCheckinWithCallingOperator = Boolean(state.selectReservation?.hasSmartCheckin && hasCallingOperatorPlugin)

  return (
    <SelfCheckinLayout
      state={state}
      header={{ goToPreviousPage: history.goBack, title: t('Guidance') }}
      footer={{
        right: {
          isDisabled: isSmartCheckinWithCallingOperator ? !isValidateHotelCode : isDisabled(),
          label: t('Next'),
          onClick: isSmartCheckinWithCallingOperator ? () => history.push({ pathname: nextPath() }) : onSubmit,
        },
      }}
    >
      {isSmartCheckinWithCallingOperator ? (
        <SelfCheckinContentCard>
          <ConfirmationWhoForm onValidate={setIsValidateHotelCode} />
        </SelfCheckinContentCard>
      ) : (
        <>
          {guides?.map((guide, index) => (
            <SelfCheckinContentCard key={guide.customCheckinId || index}>
              <CustomCheckin
                guide={guide}
                selectedInputValue={selectedInputValue.find(v => v.customCheckinId === guide.customCheckinId)}
                onChangeState={onChangeState}
              />
            </SelfCheckinContentCard>
          ))}
          {Boolean(timeSelections.length) && (
            <SelfCheckinContentCard>
              <TimeSelectionForm
                timeSelections={timeSelections}
                setIsRequiredTimeSelection={setIsRequiredTimeSelection}
                setSelectedTimes={setSelectedTimes}
                setAssignSpaces={setAssignSpaces}
              />
            </SelfCheckinContentCard>
          )}
          {hasCallingOperatorPlugin && (
            <SelfCheckinContentCard>
              <ConfirmationWhoForm onValidate={setIsValidateHotelCode} />
            </SelfCheckinContentCard>
          )}
        </>
      )}
    </SelfCheckinLayout>
  )
}
